import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faSave,
    faSpinner,
    faImage,
    faFolderOpen,
    faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { galleryService } from '../../services/galleryService';
import apiHelper from '../../utils/apiHelper';
import GalleryBrowser from '../Galleries/GalleryBrowser';
import './TalentForm.css';

// Quill modules — basic formatting toolbar
const quillModules = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['link']
    ],
    clipboard: { matchVisual: false }
};

const quillFormats = [
    'bold', 'italic', 'underline',
    'list', 'bullet', 'link'
];

function TalentForm({ talent, onSave, onClose }) {
    const [formData, setFormData] = useState({
        ID: '',
        Title: '',
        Text: '',
        Image: '',
        Link: '',
        Published: true,
    });

    const [isLoading, setIsLoading] = useState(false);
    const [galleries, setGalleries] = useState([]);
    const [showGalleryBrowser, setShowGalleryBrowser] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [isImageLoading, setIsImageLoading] = useState(false);

    useEffect(() => {
        if (talent) {
            setFormData({
                ID: talent.ID || '',
                Title: talent.Title || '',
                Text: talent.Text || '',
                Image: talent.Image || '',
                Link: talent.Link || '',
                Published: talent.Published === true || talent.Published === 'Yes' ? true : false,
            });

            if (talent.Image) {
                loadImagePreview(talent.Image);
            }
        }
    }, [talent]);

    // Cleanup blob URLs on unmount
    useEffect(() => {
        return () => {
            if (imagePreview) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, []);

    const loadImagePreview = async (imageName) => {
        setIsImageLoading(true);
        try {
            const headers = await apiHelper.getHeaders('graph/getMediaContent');
            const imageUrl = `${process.env.REACT_APP_API_URL}/api/graph/getMediaContent/${encodeURIComponent(imageName)}`;
            const response = await fetch(imageUrl, { headers });
            if (response.ok) {
                const blob = await response.blob();
                setImagePreview(URL.createObjectURL(blob));
            }
        } catch (error) {
            // Silent fail - preview not available
        } finally {
            setIsImageLoading(false);
        }
    };

    const loadGalleries = async () => {
        try {
            const galleriesData = await galleryService.getGalleries();
            setGalleries(galleriesData || []);
        } catch (error) {
            console.error('Error loading galleries:', error);
        }
    };

    const handleBrowseClick = () => {
        if (galleries.length === 0) {
            loadGalleries();
        }
        setShowGalleryBrowser(true);
    };

    const handleImageSelect = async (item) => {
        if (imagePreview) {
            URL.revokeObjectURL(imagePreview);
            setImagePreview(null);
        }

        let imageName = '';
        if (item.url && item.url.includes('/getMediaContent/')) {
            imageName = item.url.split('/getMediaContent/')[1];
        } else if (item.name || item.fileName) {
            imageName = item.name || item.fileName;
        }

        setFormData(prev => ({ ...prev, Image: imageName }));
        setShowGalleryBrowser(false);

        if (imageName) {
            loadImagePreview(imageName);
        }
    };

    const handleClearImage = () => {
        if (imagePreview) {
            URL.revokeObjectURL(imagePreview);
            setImagePreview(null);
        }
        setFormData(prev => ({ ...prev, Image: '' }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.Title?.trim()) {
            toast.error('Title is required');
            return;
        }
        if (!formData.Text?.trim()) {
            toast.error('Text is required');
            return;
        }
        if (!formData.Link?.trim()) {
            toast.error('Link is required');
            return;
        }

        setIsLoading(true);
        try {
            await onSave(formData);
        } catch (error) {
            console.error('Error saving talent:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className="talent-form-overlay">
                <div className="talent-form-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="talent-form-header">
                        <h2>{talent ? 'Edit Resource' : 'Add Resource'}</h2>
                        <button className="btn-close-form" onClick={onClose}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="talent-form-body">
                        <div className="form-group">
                            <label htmlFor="Title">Title <span className="required">*</span></label>
                            <input
                                type="text"
                                id="Title"
                                name="Title"
                                value={formData.Title}
                                onChange={handleChange}
                                placeholder="Enter talent title"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="Text">Text <span className="required">*</span></label>
                            <div className="rich-text-editor">
                                <ReactQuill
                                    value={formData.Text || ''}
                                    onChange={(content) => setFormData(prev => ({ ...prev, Text: content }))}
                                    modules={quillModules}
                                    formats={quillFormats}
                                    placeholder="Enter talent description..."
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label><FontAwesomeIcon icon={faImage} /> Image</label>
                            <div className="image-input-row">
                                <input
                                    type="text"
                                    name="Image"
                                    value={formData.Image}
                                    onChange={handleChange}
                                    placeholder="e.g. tokyo-skyline.jpg"
                                />
                                <button type="button" className="btn-browse" onClick={handleBrowseClick}>
                                    <FontAwesomeIcon icon={faFolderOpen} /> Browse
                                </button>
                                {formData.Image && (
                                    <button type="button" className="btn-clear-image" onClick={handleClearImage}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                )}
                            </div>
                            {isImageLoading && (
                                <div className="image-preview image-preview-loading">
                                    <FontAwesomeIcon icon={faSpinner} spin /> Loading preview…
                                </div>
                            )}
                            {!isImageLoading && imagePreview && (
                                <div className="image-preview">
                                    <img src={imagePreview} alt="Preview" />
                                </div>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="Link">Link <span className="required">*</span></label>
                            <input
                                type="url"
                                id="Link"
                                name="Link"
                                value={formData.Link}
                                onChange={handleChange}
                                placeholder="https://example.com"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="Published">Status</label>
                            <select
                                id="Published"
                                name="Published"
                                value={formData.Published ? 'yes' : 'no'}
                                onChange={(e) => setFormData(prev => ({ ...prev, Published: e.target.value === 'yes' }))}
                            >
                                <option value="yes">Published</option>
                                <option value="no">Draft</option>
                            </select>
                        </div>

                        <div className="talent-form-actions">
                            <button type="button" className="btn-cancel" onClick={onClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn-save" disabled={isLoading}>
                                {isLoading ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faSave} /> {talent ? 'Update' : 'Create'}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {showGalleryBrowser && (
                <GalleryBrowser
                    galleries={galleries.filter(g => g.type?.toLowerCase() === 'image')}
                    mediaType="image"
                    onSelect={handleImageSelect}
                    onClose={() => setShowGalleryBrowser(false)}
                    onRefreshGalleries={loadGalleries}
                />
            )}
        </>
    );
}

export default TalentForm;
